/**
 * MigrationCheckPanel.tsx — T17 Site-Rebuild Migration Check audit panel.
 *
 * Pure presentation. Slotted into AuditReport.tsx as a GlassCard between
 * "Top Issues" and "Audited Pages". Renders only when has_gsc_history===true;
 * renders nothing (null) for projects without Search Console data.
 *
 * CSS: uses existing .sa-audit-* classes for structural rows + new .sa-mig-*
 * classes (defined in AuditReport.css) for migration-specific layout.
 */
import React, { useState } from 'react';
import { AlertCircle, CheckCircle, ArrowRight, Copy, Check } from 'lucide-react';
import { MigrationCheckResult, MigrationMissingPage } from '../../services/api';
import './AuditReport.css';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface MigrationCheckPanelProps {
    result: MigrationCheckResult | null | undefined;
}

// ── Confidence chip ───────────────────────────────────────────────────────────

const ConfidenceChip: React.FC<{ confidence: number }> = ({ confidence }) => {
    let colorClass = 'sa-mig-confidence-chip--warning';
    let label = `${Math.round(confidence * 100)}%`;
    if (confidence >= 0.85) colorClass = 'sa-mig-confidence-chip--success';
    return (
        <span className={`sa-mig-confidence-chip ${colorClass}`} title="Redirect confidence">
            {label} match
        </span>
    );
};

// ── Copy-to-clipboard button ──────────────────────────────────────────────────

const CopyRedirectButton: React.FC<{ oldPath: string; newUrl: string }> = ({ oldPath, newUrl }) => {
    const [copied, setCopied] = useState(false);

    const getPath = (url: string): string => {
        try { return new URL(url).pathname || '/'; }
        catch { return url; }
    };

    const redirectLine = `${oldPath}  ${getPath(newUrl)}  301`;

    const handleCopy = () => {
        navigator.clipboard.writeText(redirectLine).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            // Fallback
            const el = document.createElement('textarea');
            el.value = redirectLine;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <button
            className="sa-mig-copy-btn"
            onClick={handleCopy}
            title={`Copy: ${redirectLine}`}
            aria-label="Copy redirect rule"
        >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            <span>{copied ? 'Copied' : 'Copy 301'}</span>
        </button>
    );
};

// ── Changed page row (has redirect suggestion) ────────────────────────────────

const ChangedPageRow: React.FC<{ page: MigrationMissingPage }> = ({ page }) => {
    const getPath = (url: string): string => {
        try { return new URL(url).pathname || '/'; }
        catch { return url; }
    };
    const oldPath = getPath(page.raw_url);

    return (
        <div className="sa-audit-finding-row sa-mig-changed-row">
            <span className="sa-audit-finding-category" style={{ color: 'var(--warning, #f59e0b)' }}>
                <AlertCircle size={14} />
            </span>
            <div className="sa-mig-changed-content">
                <div className="sa-mig-url-line">
                    <span className="sa-mig-old-url" title={page.raw_url}>{page.raw_url}</span>
                    <ArrowRight size={12} className="sa-mig-redirect-arrow" />
                    <span className="sa-mig-new-url" title={page.suggested_redirect_to ?? ''}>
                        {page.suggested_redirect_to}
                    </span>
                    <ConfidenceChip confidence={page.redirect_confidence} />
                </div>
                <div className="sa-mig-meta-line">
                    <span className="sa-mig-stat">{page.clicks.toLocaleString()} clicks</span>
                    <span className="sa-mig-stat">{page.impressions.toLocaleString()} impressions</span>
                    <CopyRedirectButton oldPath={oldPath} newUrl={page.suggested_redirect_to ?? ''} />
                </div>
            </div>
        </div>
    );
};

// ── Missing page row (no redirect suggestion) ─────────────────────────────────

const MissingPageRow: React.FC<{ page: MigrationMissingPage }> = ({ page }) => (
    <div className="sa-audit-finding-row sa-mig-missing-row">
        <span className="sa-audit-finding-category" style={{ color: 'var(--danger, #ef4444)' }}>
            <AlertCircle size={14} />
        </span>
        <div className="sa-mig-missing-content">
            <span className="sa-audit-finding-message sa-mig-old-url" title={page.raw_url}>
                {page.raw_url}
            </span>
            <div className="sa-mig-meta-line">
                <span className="sa-mig-stat">{page.clicks.toLocaleString()} clicks</span>
                <span className="sa-mig-stat">{page.impressions.toLocaleString()} impressions</span>
            </div>
        </div>
    </div>
);

// ── Main panel ────────────────────────────────────────────────────────────────

const MigrationCheckPanel: React.FC<MigrationCheckPanelProps> = ({ result }) => {
    // Render nothing when there's no GSC history or no result
    if (!result || !result.has_gsc_history) return null;

    const { missing_pages, changed_pages, total_lost_clicks, total_lost_impressions, window_days, live_url_count } = result;
    const totalAtRisk = missing_pages.length + changed_pages.length;
    const allClear = totalAtRisk === 0;

    return (
        <div className="sa-mig-panel">
            {/* Summary header */}
            <div className="sa-mig-header">
                <div className="sa-mig-title-row">
                    {allClear
                        ? <CheckCircle size={16} style={{ color: 'var(--success, #22c55e)', flexShrink: 0 }} />
                        : <AlertCircle size={16} style={{ color: 'var(--danger, #ef4444)', flexShrink: 0 }} />
                    }
                    <h3 className="sa-mig-title">Migration Check</h3>
                    {!allClear && (
                        <span className="sa-mig-badge">
                            {totalAtRisk} page{totalAtRisk !== 1 ? 's' : ''} at risk
                        </span>
                    )}
                </div>
                {allClear ? (
                    <p className="sa-mig-subtitle" style={{ color: 'var(--success, #22c55e)' }}>
                        No at-risk pages detected — all your ranking URLs are present on the live site.
                    </p>
                ) : (
                    <p className="sa-mig-subtitle">
                        {totalAtRisk} page{totalAtRisk !== 1 ? 's' : ''} with ranking history may be at risk &mdash;&nbsp;
                        <strong>{total_lost_clicks.toLocaleString()} clicks</strong> and{' '}
                        <strong>{total_lost_impressions.toLocaleString()} impressions</strong>{' '}
                        in the last {window_days} days could be lost.
                        Checked against {live_url_count} live URLs.
                    </p>
                )}
            </div>

            {/* Changed pages (have redirect suggestions) */}
            {changed_pages.length > 0 && (
                <div className="sa-mig-section">
                    <h4 className="sa-mig-section-title">
                        URL Changes ({changed_pages.length}) — add 301 redirects
                    </h4>
                    <p className="sa-mig-section-desc">
                        These pages appear to have moved. Copy the redirect rule for your server config or .htaccess.
                    </p>
                    <div className="sa-mig-rows">
                        {changed_pages.map((p, i) => <ChangedPageRow key={i} page={p} />)}
                    </div>
                </div>
            )}

            {/* Truly missing pages (no redirect target found) */}
            {missing_pages.length > 0 && (
                <div className="sa-mig-section">
                    <h4 className="sa-mig-section-title">
                        Missing Pages ({missing_pages.length}) — no matching URL found
                    </h4>
                    <p className="sa-mig-section-desc">
                        These pages earned clicks and impressions but have no close match on the live site.
                        Create new pages or redirect them manually.
                    </p>
                    <div className="sa-mig-rows">
                        {missing_pages.map((p, i) => <MissingPageRow key={i} page={p} />)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MigrationCheckPanel;
